import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import FlotteTab from "./FlotteTab";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Car, Plus, Search, Loader2, User, Clock, ChevronRight, Calendar, Play, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUT_COLORS = {
  actif: "bg-blue-50 text-blue-700 border-blue-200",
  cloture: "bg-[#52a675]/15 text-[#3d8a5c] border-[#52a675]/40",
  annule: "bg-gray-100 text-gray-500 border-gray-200",
};
const STATUT_LABELS = { actif: "Actif", cloture: "Clôturé", annule: "Annulé" };

function fmtDateShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function RemplacementList() {
  const navigate = useNavigate();
  const [prets, setPrets] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [flotte, setFlotte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(
    () => new URLSearchParams(window.location.search).get("tab") || "reservations");
  // iter337 — La flotte de remplacement était dans Réglages : sa place est ici
  const { user } = useAuth();
  const canManageFlotte = user?.role === "admin" || user?.role === "comptoir";
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState("");  // YYYY-MM-DD : trouve les prêts qui couvraient cette date
  // iter55 — Filtre par véhicule de flotte
  const [flotteFilter, setFlotteFilter] = useState("_all_");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/prets?limit=200"),
        api.get("/flotte"),
        api.get("/reservations?statut=reservee"),
      ]);
      setPrets(a.data.items || []);
      setFlotte(b.data || []);
      setReservations(c.data || []);
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filter = (p) => {
    if (q) {
      const s = q.toLowerCase();
      const match = (p.numero || "").toLowerCase().includes(s)
        || (p.client_nom || "").toLowerCase().includes(s)
        || (p.flotte_label || "").toLowerCase().includes(s)
        || (p.depose?.conducteur?.nom || "").toLowerCase().includes(s)
        || (p.depose?.conducteur?.prenom || "").toLowerCase().includes(s);
      if (!match) return false;
    }
    if (dateFilter) {
      // Le prêt couvrait-il dateFilter ? dépose <= D <= retour (ou aujourd'hui si actif)
      const dStart = (p.depose?.date_heure || "").slice(0, 10);
      const dEnd = (p.retour?.date_heure || new Date().toISOString()).slice(0, 10);
      if (!dStart) return false;
      if (dateFilter < dStart || dateFilter > dEnd) return false;
    }
    // iter55 — Filtre véhicule de remplacement
    if (flotteFilter && flotteFilter !== "_all_" && p.flotte_id !== flotteFilter) return false;
    return true;
  };

  const filterRes = (r) => {
    if (q) {
      const s = q.toLowerCase();
      const match = (r.flotte_label || "").toLowerCase().includes(s)
        || (r.commentaire || "").toLowerCase().includes(s)
        || (r.client_nom || "").toLowerCase().includes(s);
      if (!match) return false;
    }
    if (dateFilter) {
      const dStart = (r.date_debut || "").slice(0, 10);
      const dEnd = (r.date_fin || "").slice(0, 10);
      if (!dStart || !dEnd) return false;
      if (dateFilter < dStart || dateFilter > dEnd) return false;
    }
    if (flotteFilter && flotteFilter !== "_all_" && r.flotte_id !== flotteFilter) return false;
    return true;
  };

  const actifs = prets.filter((p) => p.statut === "actif").filter(filter);
  const clotures = prets.filter((p) => p.statut !== "actif").filter(filter);
  const futures = reservations.filter(filterRes);

  const cancelReservation = async (r) => {
    if (!window.confirm(`Annuler la réservation ?\n\n${r.flotte_label}\n${r.client_nom || "Client non renseigné"}\n${(r.date_debut || "").slice(0, 10)} → ${(r.date_fin || "").slice(0, 10)}`)) return;
    try {
      await api.delete(`/reservations/${r.id}`);
      toast.success("Réservation annulée");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const stats = {
    total: flotte.length,
    dispo: flotte.filter((f) => f.statut === "disponible" && f.actif).length,
    en_pret: flotte.filter((f) => f.statut === "en_pret").length,
    maint: flotte.filter((f) => f.statut === "maintenance").length,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="remplacement-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Car className="w-7 h-7 text-[#52a675]" /> Véhicules de remplacement
          </h1>
          <p className="text-sm text-gray-500 mt-1">Prêts courtoisie et locations courtes durées</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/planning-flotte")}
            data-testid="pret-agenda-button"
          >
            <Calendar className="w-4 h-4 mr-2" /> Agenda des véhicules
          </Button>
          <Button onClick={() => navigate("/remplacement/nouveau")} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="pret-new-button">
            <Plus className="w-4 h-4 mr-2" /> Nouveau prêt
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4 border-gray-200"><div className="text-xs uppercase text-gray-500 font-semibold">Flotte totale</div><div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div></Card>
        <Card className="p-4 border-[#52a675]/30 bg-[#52a675]/5"><div className="text-xs uppercase text-[#3d8a5c] font-semibold">Disponibles</div><div className="text-2xl font-bold text-[#2d5f3f] mt-1">{stats.dispo}</div></Card>
        <Card className="p-4 border-blue-200 bg-blue-50"><div className="text-xs uppercase text-blue-700 font-semibold">En prêt</div><div className="text-2xl font-bold text-blue-900 mt-1">{stats.en_pret}</div></Card>
        <Card className="p-4 border-orange-200 bg-orange-50"><div className="text-xs uppercase text-orange-700 font-semibold">Maintenance</div><div className="text-2xl font-bold text-orange-900 mt-1">{stats.maint}</div></Card>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (n°, plaque, marque, conducteur…)"
            className="pl-10"
            data-testid="pret-search"
          />
        </div>
        <div className="flex flex-wrap items-end gap-2 min-w-0">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Date concernée
            </label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full sm:w-44"
              data-testid="pret-date-filter"
              title="Trouve les prêts qui couvraient cette date (utile pour les contraventions)"
            />
          </div>
          {/* iter55 — Filtre véhicule de remplacement */}
          <div className="flex-1 min-w-[150px]">
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Véhicule
            </label>
            <select
              value={flotteFilter}
              onChange={(e) => setFlotteFilter(e.target.value)}
              className="h-9 rounded-md border border-gray-300 bg-white text-sm px-2 w-full sm:w-48"
              data-testid="pret-flotte-filter"
            >
              <option value="_all_">Tous les véhicules</option>
              {flotte.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.plaque} · {f.marque} {f.modele}
                </option>
              ))}
            </select>
          </div>
          {(q || dateFilter || (flotteFilter && flotteFilter !== "_all_")) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setQ(""); setDateFilter(""); setFlotteFilter("_all_"); }}
              className="h-9 text-xs"
              data-testid="pret-clear-filters"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Effacer
            </Button>
          )}
        </div>
        {dateFilter && (
          <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
            Affiche tous les prêts/réservations couvrant le <strong>{new Date(dateFilter).toLocaleDateString("fr-CH")}</strong>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          {/* iter256 — mobile : menu déroulant au lieu de la bande d'onglets */}
          <div className="sm:hidden mb-3">
            <Select value={tab} onValueChange={setTab}>
              <SelectTrigger className="w-full" data-testid="pret-tab-menu">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reservations" data-testid="pret-tab-menu-reservations">Réservations à venir ({futures.length})</SelectItem>
                <SelectItem value="actifs" data-testid="pret-tab-menu-actifs">Actifs ({actifs.length})</SelectItem>
                <SelectItem value="clotures" data-testid="pret-tab-menu-clotures">Historique ({clotures.length})</SelectItem>
                {canManageFlotte && (
                  <SelectItem value="flotte" data-testid="pret-tab-menu-flotte">Flotte de remplacement</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <TabsList className="hidden sm:flex">
            <TabsTrigger value="reservations" data-testid="tab-reservations">Réservations à venir ({futures.length})</TabsTrigger>
            <TabsTrigger value="actifs" data-testid="tab-actifs">Actifs ({actifs.length})</TabsTrigger>
            <TabsTrigger value="clotures" data-testid="tab-clotures">Historique ({clotures.length})</TabsTrigger>
            {canManageFlotte && (
              <TabsTrigger value="flotte" data-testid="tab-flotte">Flotte de remplacement</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="reservations" className="mt-4">
            {futures.length === 0 ? (
              <Card className="p-10 text-center border-dashed border-2 border-gray-200">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Aucune réservation à venir.</p>
                <p className="text-xs text-gray-400 mt-1">Les réservations créées via le widget RDV public apparaîtront ici automatiquement.</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {futures.map((r) => (
                  <Card
                    key={r.id}
                    className="p-4 border-amber-200 bg-amber-50/30"
                    data-testid={`reservation-row-${r.id}`}
                  >
                    <div className="flex items-center gap-4 flex-wrap">
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                        <Calendar className="w-3 h-3 mr-1" /> Réservé
                      </Badge>
                      <div className="flex-1 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Car className="w-3.5 h-3.5 text-gray-400" />
                          <span className="font-semibold">{r.flotte_label}</span>
                        </div>
                        {r.client_nom && (
                          <div className="flex items-center gap-1 text-xs text-gray-700">
                            <User className="w-3 h-3" /> {r.client_nom}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <Clock className="w-3 h-3" /> {fmtDateShort(r.date_debut)} → {fmtDateShort(r.date_fin)}
                        </div>
                        {r.source === "widget_public_auto" && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 text-[10px]">Plugin web</Badge>
                        )}
                        {r.tarif_jour > 0 && (
                          <span className="text-xs font-mono text-gray-600">{r.tarif_jour} CHF/j</span>
                        )}
                        {r.tarif_jour === 0 && (
                          <span className="text-xs font-semibold text-emerald-700">Offert</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-auto">
                        {r.rdv_id && (
                          <button
                            type="button"
                            onClick={() => navigate("/calendrier")}
                            className="text-xs text-blue-600 hover:underline"
                            data-testid={`reservation-voir-rdv-${r.id}`}
                          >
                            Voir RDV →
                          </button>
                        )}
                        <Button
                          size="sm"
                          className="bg-[#52a675] hover:bg-[#3d8a5c]"
                          onClick={() => navigate("/remplacement/nouveau", { state: { reservation: r } })}
                          data-testid={`reservation-demarrer-${r.id}`}
                        >
                          <Play className="w-3.5 h-3.5 mr-1" /> Démarrer le prêt
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-9 w-9 border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => cancelReservation(r)}
                          title="Annuler la réservation"
                          data-testid={`reservation-cancel-${r.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {r.commentaire && (
                      <div className="text-[11px] text-gray-500 mt-2 ml-1">{r.commentaire}</div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="actifs" className="mt-4">
            {actifs.length === 0 ? (
              <Card className="p-10 text-center border-dashed border-2 border-gray-200">
                <p className="text-sm text-gray-500 mb-3">Aucun prêt en cours.</p>
                <Button onClick={() => navigate("/remplacement/nouveau")} className="bg-[#52a675] hover:bg-[#3d8a5c]">
                  <Plus className="w-4 h-4 mr-2" /> Créer un prêt
                </Button>
              </Card>
            ) : (
              <div className="space-y-2">
                {actifs.map((p) => (
                  <Card
                    key={p.id}
                    onClick={() => navigate(`/remplacement/${p.id}`)}
                    className="p-4 border-gray-200 hover:border-[#52a675]/40 hover:shadow-md cursor-pointer transition-all"
                    data-testid={`pret-row-${p.id}`}
                  >
                    <div className="flex items-center gap-4 flex-wrap">
                      <code className="text-sm font-bold font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded">{p.numero}</code>
                      <Badge variant="outline" className={STATUT_COLORS[p.statut]}>{STATUT_LABELS[p.statut]}</Badge>
                      <div className="flex-1 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-1.5 text-sm">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span className="font-semibold">{p.client_nom}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Car className="w-3.5 h-3.5 text-gray-400" />
                          <span>{p.flotte_label}</span>
                        </div>
                        {p.depose?.date_retour_prevu && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="w-3 h-3" /> Retour prévu {fmtDateShort(p.depose.date_retour_prevu)}
                          </div>
                        )}
                        {!p.permis_photo_recto_present && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]" data-testid={`pret-permis-missing-${p.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" /> Permis manquant
                          </Badge>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="clotures" className="mt-4">
            {clotures.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-6">Aucun historique.</div>
            ) : (
              <div className="space-y-2">
                {clotures.map((p) => (
                  <Card
                    key={p.id}
                    onClick={() => navigate(`/remplacement/${p.id}`)}
                    className="p-3 border-gray-200 hover:border-[#52a675]/40 hover:shadow-sm cursor-pointer transition-all"
                    data-testid={`pret-row-${p.id}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap text-sm">
                      <code className="text-xs font-bold font-mono text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded">{p.numero}</code>
                      <Badge variant="outline" className={STATUT_COLORS[p.statut]}>{STATUT_LABELS[p.statut]}</Badge>
                      <span className="font-semibold">{p.client_nom}</span>
                      <span className="text-gray-500">· {p.flotte_label}</span>
                      {!p.permis_photo_recto_present && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]" data-testid={`pret-permis-missing-${p.id}`}>
                          <AlertTriangle className="w-3 h-3 mr-1" /> Permis manquant
                        </Badge>
                      )}
                      {p.retour?.total_facture > 0 && (
                        <span className="ml-auto font-mono font-semibold">{p.retour.total_facture.toFixed(2)} CHF</span>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {canManageFlotte && (
            <TabsContent value="flotte" className="mt-4" data-testid="remplacement-flotte-tab">
              <FlotteTab />
            </TabsContent>
          )}

        </Tabs>
      )}
    </div>
  );
}
